import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { FileDown, FileText, BookOpen, GraduationCap, ArrowUpRight, Mail, FileQuestion, WifiOff, Wrench, Heart, CheckCircle2, ShoppingCart, PlayCircle, LifeBuoy } from 'lucide-react';
import { Button, Container, IconTile, Reveal, SectionHeading, TextLink } from '@/components/ui';
import { CtaBand, PageHero, TrainingBand } from '@/components/sections';
import { Alert, TextArea, TextField } from '@/components/form';
import { usePageTitle } from '@/components/layout/SiteLayout';
import { INDUSTRIES } from '@/content/industries';
import { findModule } from '@/content/platform';
import { pdfSlugs } from '@/pdf/PdfRouter';
import { INK_U_URL, SALES_EMAIL, SUPPORT_EMAIL, orderLink, HELP_URL } from '@/lib/site';

export function IndustriesPage() {
  usePageTitle('Industries');
  return (
    <>
      <PageHero
        crumbs={[{ label: 'Industries' }]}
        eyebrow="Industries"
        title="One platform, tuned to how your industry runs"
        intro="Scada.Ink is used wherever machines need watching and numbers need trusting — from a single CNC cell to a remote pump station."
        actions={
          <Button to={orderLink('demo')} size="lg" arrow>
            Book an industry demo
          </Button>
        }
      />
      <section className="py-16 lg:py-20">
        <Container className="space-y-6">
          {INDUSTRIES.map((ind, i) => (
            <Reveal key={ind.slug}>
              <article id={ind.slug} className="scroll-mt-32 glass-panel p-6 lg:p-8 grid lg:grid-cols-[260px_1fr_1fr] gap-6 lg:gap-10">
                <div>
                  <IconTile icon={ind.icon} color={i % 2 ? '#7c3aed' : '#3b82f6'} size="lg" />
                  <h2 className="mt-4 text-2xl font-bold">{ind.name}</h2>
                </div>
                <div>
                  <h3 className="text-xs font-semibold uppercase tracking-[0.16em] text-status-fault">The challenge</h3>
                  <p className="mt-2 text-text-secondary leading-relaxed">{ind.challenge}</p>
                </div>
                <div>
                  <h3 className="text-xs font-semibold uppercase tracking-[0.16em] text-status-running">With Scada.Ink</h3>
                  <p className="mt-2 text-text-secondary leading-relaxed">{ind.solution}</p>
                  <div className="mt-4 flex flex-wrap gap-2">
                    {ind.modules.map((slug) => {
                      const m = findModule(slug)!;
                      return (
                        <Link key={slug} to={`/platform/${slug}`} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-ink-muted border border-glass-border text-xs font-medium hover:border-brand-primary/40 hover:text-brand-primary transition-colors">
                          <m.icon size={12} /> {m.name}
                        </Link>
                      );
                    })}
                  </div>
                </div>
              </article>
            </Reveal>
          ))}
        </Container>
      </section>
      <CtaBand title="Talk to someone who knows your floor" body="Tell us your industry and equipment and we will show you a demo built around it." />
    </>
  );
}

export function ResourcesPage() {
  usePageTitle('Resources');
  return (
    <>
      <PageHero
        crumbs={[{ label: 'Resources' }]}
        eyebrow="Resources"
        title="Everything you need to evaluate, learn and run Scada.Ink"
        intro="Download the overview and feature datasheets, read the documentation in the Help Center, and learn the platform free on Ink U."
      />
      <section className="py-16 lg:py-20">
        <Container>
          <div className="grid md:grid-cols-3 gap-5">
            <div className="glass-panel p-7 flex flex-col">
              <IconTile icon={FileDown} color="#3b82f6" />
              <h2 className="mt-4 text-lg font-bold">Platform overview</h2>
              <p className="mt-2 text-sm text-text-secondary flex-1">The complete platform in one PDF — ideal for sharing with your team and management.</p>
              <div className="mt-5">
                <Button href="/overview.pdf" download>
                  <FileDown size={15} /> Download PDF
                </Button>
              </div>
            </div>
            <div className="glass-panel p-7 flex flex-col">
              <IconTile icon={GraduationCap} color="#7c3aed" />
              <h2 className="mt-4 text-lg font-bold">Ink U training</h2>
              <p className="mt-2 text-sm text-text-secondary flex-1">Free video courses, quizzes and certification for operators, engineers and integrators.</p>
              <div className="mt-5">
                <Button href={INK_U_URL}>
                  Open Ink U <ArrowUpRight size={14} />
                </Button>
              </div>
            </div>
            <div id="docs" className="scroll-mt-32 glass-panel p-7 flex flex-col">
              <IconTile icon={BookOpen} color="#16a34a" />
              <h2 className="mt-4 text-lg font-bold">Help Center</h2>
              <p className="mt-2 text-sm text-text-secondary flex-1">
                Step-by-step documentation for every feature, searchable and kept current. The same help is built into every appliance — open <b>Help</b> from any page, even with no internet connection.
              </p>
              <div className="mt-5 flex flex-wrap gap-2">
                <Button href={HELP_URL}>
                  Open the Help Center <ArrowUpRight size={14} />
                </Button>
                <Button to="/contact" variant="secondary">
                  <LifeBuoy size={15} /> Contact support
                </Button>
              </div>
            </div>
          </div>
        </Container>
      </section>

      <section className="py-16 bg-white border-y border-glass-border">
        <Container>
          <SectionHeading eyebrow="Datasheets" title="Feature datasheets" intro="One-page PDFs on each part of the platform, ready to download and share." />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {/* Generated by `npm run pdfs` from the sheets in src/pdf. */}
            {pdfSlugs.map((p) => (
              <a key={p.slug} href={`/datasheets/${p.slug}.pdf`} target="_blank" rel="noopener noreferrer" className="group flex items-center gap-3 glass-card p-4 hover:shadow-md transition-shadow">
                <FileText size={18} className="text-brand-primary shrink-0" />
                <span className="flex-1 text-sm font-semibold group-hover:text-brand-primary transition-colors">{p.label}</span>
                <FileDown size={14} className="text-text-muted" />
              </a>
            ))}
          </div>
        </Container>
      </section>

      <TrainingBand />
    </>
  );
}

export function CompanyPage() {
  usePageTitle('Company');
  return (
    <>
      <PageHero
        crumbs={[{ label: 'Company' }]}
        eyebrow="About Scada.Ink"
        title="Built for the factory floor, by people who have stood on it"
        intro="Scada.Ink exists because monitoring a plant should not require a cloud contract, a server room or a six-month integration project."
      />
      <section className="py-20">
        <Container>
          <div className="grid md:grid-cols-3 gap-5">
            {[
              { icon: WifiOff, title: 'Edge first', body: 'The plant keeps running when the internet or the corporate network goes down. Every core feature works fully offline, on hardware in your panel, and setup typically doesn’t need IT involved.' },
              { icon: Wrench, title: 'Practical over clever', body: 'Features earn their place by saving an engineer time on a real project — connecting, commissioning, troubleshooting.' },
              { icon: Heart, title: 'Teach everyone', body: 'Training is free because a platform nobody knows how to use is not worth buying. Ink U is open to anyone.' },
            ].map((v) => (
              <div key={v.title} className="glass-card p-7">
                <v.icon size={24} className="text-brand-primary" />
                <h2 className="mt-4 text-lg font-bold">{v.title}</h2>
                <p className="mt-2 text-sm text-text-secondary leading-relaxed">{v.body}</p>
              </div>
            ))}
          </div>
        </Container>
      </section>
      <CtaBand />
    </>
  );
}

export function ContactPage() {
  usePageTitle('Contact');
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setSending(true);
    setError(null);
    try {
      const res = await fetch('/api/contact.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, subject: form.subject || undefined }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Failed to send message');
      setSent(true);
    } catch (err) {
      setError((err as Error).message || 'Something went wrong. Please try again.');
    } finally {
      setSending(false);
    }
  }

  return (
    <>
      <PageHero crumbs={[{ label: 'Contact' }]} eyebrow="Contact" title="How can we help?" intro="Sales, support, partnerships or certification verification — send a message and the right person will reply." />
      <section className="py-16 lg:py-20">
        <Container>
          <div className="grid lg:grid-cols-[1fr_1.4fr] gap-10">
            <div className="space-y-4">
              {[
                { icon: ShoppingCart, title: 'Buying Scada.Ink?', body: 'The order form gets you a quote fastest.', cta: 'Request a quote', to: orderLink('quote') },
                { icon: PlayCircle, title: 'Want to see it?', body: 'Book a live demo with an engineer.', cta: 'Book a demo', to: orderLink('demo') },
                { icon: GraduationCap, title: 'Learning the platform?', body: 'Ink U has free courses for every feature.', cta: 'Open Ink U', to: INK_U_URL },
              ].map((c) => (
                <div key={c.title} className="glass-card p-5 flex items-start gap-4">
                  <IconTile icon={c.icon} color="#3b82f6" size="sm" />
                  <div>
                    <div className="font-semibold">{c.title}</div>
                    <div className="text-sm text-text-secondary mt-0.5">{c.body}</div>
                    <div className="mt-2">
                      <TextLink to={c.to}>{c.cta}</TextLink>
                    </div>
                  </div>
                </div>
              ))}
              <div className="glass-card p-5 space-y-2 text-sm">
                <a href={`mailto:${SALES_EMAIL}`} className="flex items-center gap-2 text-text-secondary hover:text-brand-primary">
                  <Mail size={15} /> Sales: {SALES_EMAIL}
                </a>
                <a href={`mailto:${SUPPORT_EMAIL}`} className="flex items-center gap-2 text-text-secondary hover:text-brand-primary">
                  <Mail size={15} /> Support: {SUPPORT_EMAIL}
                </a>
              </div>
            </div>
            <div className="glass-panel p-6 lg:p-8">
              {sent ? (
                <div className="text-center py-10">
                  <CheckCircle2 size={44} className="mx-auto text-status-running" />
                  <h2 className="mt-4 text-xl font-bold">Message sent</h2>
                  <p className="mt-2 text-text-secondary">We will get back to you as soon as possible.</p>
                  <div className="mt-6">
                    <Button
                      variant="secondary"
                      onClick={() => {
                        setForm({ name: '', email: '', subject: '', message: '' });
                        setSent(false);
                      }}
                    >
                      Send another
                    </Button>
                  </div>
                </div>
              ) : (
                <form onSubmit={submit} className="space-y-4">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <TextField label="Name" required autoComplete="name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                    <TextField label="Email" type="email" required autoComplete="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                  </div>
                  <TextField label="Subject" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} />
                  <TextArea label="Message" required rows={6} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} />
                  {error && <Alert kind="error">{error}</Alert>}
                  <div className="flex justify-end">
                    <Button type="submit" size="lg" disabled={sending} arrow>
                      {sending ? 'Sending…' : 'Send message'}
                    </Button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </Container>
      </section>
    </>
  );
}

export function NotFoundPage() {
  usePageTitle('Page not found');
  return (
    <Container className="py-28 text-center">
      <FileQuestion size={48} className="mx-auto text-text-muted" />
      <h1 className="mt-5 text-3xl font-bold">We couldn’t find that page</h1>
      <p className="mt-2 text-text-secondary">It may have moved when the site was reorganized.</p>
      <div className="mt-8 flex flex-wrap justify-center gap-3">
        <Button to="/" arrow>
          Home
        </Button>
        <Button to="/platform" variant="secondary">
          Platform
        </Button>
        <Button to="/contact" variant="secondary">
          Contact
        </Button>
      </div>
    </Container>
  );
}
